import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, 
  Plus, 
  User, 
  Wallet, 
  Edit2, 
  Trash2, 
  Building,
  Search,
  Euro
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartiteIVA = () => {
  const { user, isSindacatoAdmin, hasPermesso } = useAuth();
  const [partite, setPartite] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPartita, setSelectedPartita] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    descrizione: '',
    direttore_id: ''
  });

  const isAdmin = isSindacatoAdmin();
  const canCreate = isAdmin || hasPermesso('gestisci_conti');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partiteRes, utentiRes] = await Promise.all([
        axios.get(`${API_URL}/partite-iva`),
        canCreate ? axios.get(`${API_URL}/utenti-per-partita-iva`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setPartite(partiteRes.data);
      setUtenti(utentiRes.data);
    } catch (error) {
      console.error('Errore caricamento:', error);
      toast.error('Errore nel caricamento delle partite IVA');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartite = partite.filter(p =>
    p.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.direttore_nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.direttore_username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.direttore_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      await axios.post(`${API_URL}/partite-iva`, form);
      toast.success('Partita IVA creata con successo');
      setDialogOpen(false);
      setForm({ nome: '', descrizione: '', direttore_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/partite-iva/${selectedPartita.id}`, {
        nome: form.nome || undefined,
        descrizione: form.descrizione || undefined,
        direttore_id: form.direttore_id || undefined
      });
      toast.success('Partita IVA aggiornata');
      setEditDialogOpen(false);
      setSelectedPartita(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    }
  };

  const handleDelete = async (partita) => {
    if (!confirm(`Sei sicuro di voler eliminare la partita IVA "${partita.nome}"?`)) return;
    try {
      await axios.delete(`${API_URL}/partite-iva/${partita.id}`);
      toast.success('Partita IVA eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella eliminazione');
    }
  };

  const openEditDialog = (partita) => {
    setSelectedPartita(partita);
    setForm({
      nome: partita.nome,
      descrizione: partita.descrizione || '',
      direttore_id: partita.direttore_id
    });
    setEditDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="partite-iva-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-emerald-600" />
            Partite IVA
          </h1>
          <p className="text-slate-600 mt-1">
            Gestione conti aziendali per liberi professionisti e attività private
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Partita IVA
          </Button>
        )}
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{partite.length}</p>
            <p className="text-sm text-emerald-600">Partite IVA Totali</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">
              {formatCurrency(partite.reduce((sum, p) => sum + (p.saldo || 0), 0))}
            </p>
            <p className="text-sm text-blue-600">Saldo Totale</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">
              {partite.filter(p => p.saldo > 0).length}
            </p>
            <p className="text-sm text-amber-600">Con Saldo Positivo</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-700">
              {new Set(partite.map(p => p.direttore_id)).size}
            </p>
            <p className="text-sm text-slate-600">Titolari Unici</p>
          </CardContent>
        </Card>
      </div>

      {/* Barra di ricerca */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per nome o titolare..."
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista Partite IVA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Elenco Partite IVA ({filteredPartite.length})
          </CardTitle>
          <CardDescription>
            Conti aziendali gestiti dai rispettivi titolari
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredPartite.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{searchQuery ? 'Nessuna partita IVA trovata' : 'Nessuna partita IVA registrata'}</p>
              {canCreate && !searchQuery && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crea la prima Partita IVA
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome Attività</TableHead>
                    <TableHead>Titolare</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartite.map((partita) => (
                    <TableRow key={partita.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{partita.nome}</p>
                            <p className="text-xs text-slate-500">
                              Creata: {new Date(partita.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">@{partita.direttore_username}</p>
                            <p className="text-xs text-slate-500">{partita.direttore_nome}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className={`${partita.saldo >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          <Euro className="w-3 h-3 mr-1" />
                          {formatCurrency(partita.saldo)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 max-w-xs truncate">
                          {partita.descrizione || '-'}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {(isAdmin || partita.direttore_id === user?.id) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={() => openEditDialog(partita)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(partita)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Creazione */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-600 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Nuova Partita IVA
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nome Attività *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Es: Studio Legale Rossi, Officina Bianchi..."
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                placeholder="Breve descrizione dell'attività..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Titolare (Direttore) *</Label>
              <select
                value={form.direttore_id}
                onChange={(e) => setForm({ ...form, direttore_id: e.target.value })}
                className="w-full p-2 border rounded-lg bg-white"
                required
              >
                <option value="">Seleziona il titolare...</option>
                {utenti.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                Il titolare potrà gestire il conto bancario associato
              </p>
            </div>

            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-700">
                <Wallet className="w-4 h-4 inline mr-2" />
                Verrà creato automaticamente un conto bancario con saldo €0,00
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Crea Partita IVA
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-600 flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Modifica Partita IVA
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nome Attività</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Nome attività..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                placeholder="Descrizione..."
                rows={2}
              />
            </div>
            
            {isAdmin && (
              <div className="space-y-2">
                <Label>Cambia Titolare</Label>
                <select
                  value={form.direttore_id}
                  onChange={(e) => setForm({ ...form, direttore_id: e.target.value })}
                  className="w-full p-2 border rounded-lg bg-white"
                >
                  <option value="">Mantieni titolare attuale</option>
                  {utenti.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Edit2 className="w-4 h-4 mr-2" />
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartiteIVA;
